import moment from 'moment';
import type React from 'react';
import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';
import { toast } from 'react-toastify';
import {
  ImageSliderModalCloseIcon, ImageSliderNextIcon, ImageSliderPreviousIcon, DownloadIcon,
} from 'src/assets/images/svg-icons';

import ThumbnailCarousel from 'src/components/shared/ThumbnailCarousel';
import ImageWithLoader from 'src/components/shared/image-with-loader';
import VideoPlayer from 'src/components/shared/video-player';

import { useAppDispatch, useAppSelector } from 'src/hooks/redux-hooks';
import { updateVisibility } from 'src/redux/modals/slice';
import { getAllPhotos } from 'src/redux/jobs/api';

interface SlideItem {
  attachment_id?: number | string | null;
  media_type?: string;
  thumbnail_url?: string;
  attachment_optimized_url?: string;
  job_media_thumbnail_url?: string;
  attachment_local_url?: string;
  url?: string;
  attachment_url?: string;
  attachment_download_url?: string | null;
  // PH-4902: attachments and job media each carry their own forced-download link.
  job_media_download_url?: string | null;
  job_media_id?: number | string | null;
  created_at?: string;
  [key: string]: unknown;
}

interface DownloadingState {
  before: boolean;
  after: boolean;
  'tenant-photos': boolean;
  [key: string]: boolean;
}

const headerText: Record<string, { title: string; slug: string }> = {
  'before-photos': { title: 'Before #', slug: 'before' },
  'after-photos': { title: 'After #', slug: 'after' },
  'tenant-photos': { title: 'Tenant #', slug: 'tenant-photos' },
};

const ImageSliderModal = () => {
  const dispatch = useAppDispatch();
  const {
    imageSlidesModal: { extraProps },
  } = useAppSelector(
    (state: Record<string, unknown>) => (
      state.modals as Record<string, unknown>
    ),
  ) as {
    imageSlidesModal: {
      extraProps: Record<string, unknown>;
    };
  };

  const handleModalClose = useCallback(() => {
    dispatch(
      updateVisibility({
        modalType: 'imageSlidesModal',
        isVisible: false,
      }),
    );
  }, [dispatch]);

  const videoElement = useRef<HTMLVideoElement | null>(null);

  const { modalData } = extraProps || {};
  const md = modalData as Record<string, unknown> | undefined;
  const {
    collection, position, imageType, timeZone, hasAbilityToDownload = false,
  } = md || {};

  const [slides, setSlides] = useState<SlideItem[]>((collection as SlideItem[]) || []);
  const [activeImage, setActiveImage] = useState<number>(
    (collection as SlideItem[])?.[position as number]
      ? (position as number) : 0,
  );

  useEffect(() => {
    setSlides(collection as SlideItem[]);
  }, [collection]);

  const showNextImage = useCallback(() => {
    setActiveImage((index) => {
      if (index === slides.length - 1) return 0;
      return index + 1;
    });
  }, [slides.length]);

  const showPrevImage = useCallback(() => {
    setActiveImage((index) => {
      if (index === 0) return slides.length - 1;
      return index - 1;
    });
  }, [slides.length]);

  const handleVideoPlay = useCallback(() => {
    if (videoElement?.current) {
      if (videoElement?.current?.paused) {
        videoElement.current.play();
      } else {
        videoElement.current.pause();
      }
    }
  }, []);

  const renderImages = useCallback(() => {
    const mediaType = slides?.[activeImage]?.media_type;

    const thumbnailUrl = slides?.[activeImage]?.thumbnail_url
    || slides?.[activeImage]?.attachment_optimized_url
    || slides?.[activeImage]?.job_media_thumbnail_url
    || slides?.[activeImage]?.attachment_local_url;

    const mediaUrl = slides?.[activeImage]?.url || slides?.[activeImage]?.attachment_url;

    if (mediaType === 'video' && mediaUrl) {
      const slide = slides?.[activeImage];
      return (
        <VideoPlayer
          key={mediaUrl}
          ref={videoElement}
          src={mediaUrl}
          downloadUrl={slide?.attachment_download_url ?? slide?.job_media_download_url}
          mediaId={slide?.attachment_id ?? slide?.job_media_id}
          source="image-slider-modal"
        />
      );
    }

    if (
      (thumbnailUrl
    || mediaUrl)) {
      return (
        <ImageWithLoader
          key={`img_s${activeImage}`}
          originalImg={mediaUrl as string}
          thumbnail={thumbnailUrl}
        />
      );
    }

    return <p style={{ color: 'white' }}>Unsupported file format.</p>;
  }, [activeImage, slides]);

  const handleSideArrowActions = useCallback((event: KeyboardEvent) => {
    if (event.key === 'ArrowLeft') {
      showPrevImage();
    } else if (event.key === 'ArrowRight') {
      showNextImage();
    } else if (event.code === 'Space') {
      handleVideoPlay();
    }
  }, [handleVideoPlay, showNextImage, showPrevImage]);

  useEffect(() => {
    document.addEventListener('keydown', handleSideArrowActions);

    return () => {
      document.removeEventListener('keydown', handleSideArrowActions);
    };
  }, [handleSideArrowActions]);

  const [isDownloading, setIsDownloading] = useState<DownloadingState>({
    before: false,
    after: false,
    'tenant-photos': false,
  });

  const downloadAllPhotos = useCallback((type: string) => {
    setIsDownloading((prev) => ({ ...prev, [type]: true }));
    dispatch(
      getAllPhotos(md?.jobRootNodeId as string, type)({}),
    ).unwrap().then((response: unknown) => {
      const res = response as Record<string, unknown>;
      if (res?.export_url) {
        window.open(res?.export_url as string);
      }
      setIsDownloading((prev) => ({ ...prev, [type]: false }));
    })?.catch((e: unknown) => {
      const err = e as Record<string, unknown>;
      toast((err?.message as string) || 'Something went wrong', { type: 'error' });
      setIsDownloading((prev) => ({ ...prev, [type]: false }));
    });
  }, [dispatch, md?.jobRootNodeId]);

  return (
    <Modal
      isOpen
      centered
      toggle={handleModalClose}
      className="slider-image-modal"
      modalTransition={{ timeout: 0 }}
      backdropTransition={{ timeout: 0 }}
    >
      <ModalHeader>

        {headerText?.[imageType as string] ? headerText?.[imageType as string]?.title : '' }
        {headerText?.[imageType as string] ? `${activeImage + 1} -` : <div />}
        {' '}
        {(headerText?.[imageType as string]
          && slides?.[activeImage]?.created_at)
          && timeZone
          ? moment.utc(
            slides?.[activeImage]?.created_at,
            'YYYY-MM-DD HH:mm:ss',
          ).tz(timeZone as string)
            .format('MMM D, YYYY')
          : <div />}
        {' '}
        <div className="btn-wrap">
          {headerText?.[imageType as string] && hasAbilityToDownload ? (
            <Button
              className="download-btn"
              color="deoco"
              onClick={() => downloadAllPhotos(headerText?.[imageType as string]?.slug)}
              disabled={isDownloading?.[headerText?.[imageType as string]?.slug]}
            >
              <DownloadIcon />
              Download All
            </Button>
          ) : <div />}
          <Button
            color="deoco"
            onClick={handleModalClose}
            className="btn-close"
          >
            <ImageSliderModalCloseIcon strokeColor="white" />
          </Button>
        </div>
      </ModalHeader>
      <ModalBody>
        <div className="image-gallery-slider">
          {/* <div className="slider-modal-action">
            {(modalData?.showDeleteButton && ((slides?.length > 1)
            || (modalData?.enableDeleteForLastItem))) ? (
              <Button
                onClick={handleRemove}
                className="action-btn delete"
              >
                <ImageSliderDeleteSvg />
              </Button>
              )
              : ''}
          </div> */}
          <div className="image-gallery-preview">
            {renderImages()}
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <div className="image-gallery-slider">
          <div className="image-gallery-thumbnail">
            <ThumbnailCarousel
              slides={slides as unknown as React.ComponentProps<typeof ThumbnailCarousel>['slides']}
              activeImage={activeImage}
              onThumbnailClick={setActiveImage}
            />
          </div>
          <div className="image-gallery-actions">
            {slides?.length > 10 && activeImage > 0 ? (
              <Button
                color="deoco"
                className="left"
                onClick={showPrevImage}
              >
                <ImageSliderPreviousIcon />
              </Button>
            ) : ''}
            {slides?.length > 10 && activeImage < slides.length - 1 ? (
              <Button
                className="right"
                color="deoco"
                onClick={showNextImage}
              >
                <ImageSliderNextIcon />
              </Button>
            ) : ''}
          </div>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default ImageSliderModal;
